import React, { useEffect, useState, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import Link from 'next/link';
import Head from 'next/head';
import Router from 'next/router';

import { useToasts } from 'react-toast-notifications';

import CoveApi from 'utilities/coveApi2';

import { Mixpanel } from 'utilities/mixpanel';

import queryString from 'query-string';
import { AddFreshChat } from '/utilities/FreshChat';

import { Context } from 'hocs/context';
import { CustomerContext } from 'state/Customer.context';

import { formatError, noError } from 'hocs/validation/errors';

import logger from 'utilities/logger';

import { clearMultipleLocalStorage } from 'utilities/utilities';

// HOCs
import useQueryStringProduct from 'hocs/state/useQueryStringProduct';

// Components
import Section from 'components/layout/Section';
import Title from 'components/label/Title';
import Message from 'components/label/Message';
import Subtitle from 'components/label/Subtitle';
import SubmitButton from 'components/button/SubmitButton';
import NameField from 'components/field/NameField';
import ProgressBar from 'components/ProgressBar';
import FormContainer from 'components/layout/FormContainer';
import BackChevron from 'components/button/BackChevron';
import ContentCards from 'components/card/ContentCards';
import FooterPeopleFlockToCove from 'components/layout/FooterPeopleFlockToCove';
import FooterLearnMoreAboutCarIns from 'components/layout/FooterLearnMoreAboutCarIns';
import NavBar from 'components/NavBar';

// Icons
import Svg from 'components/label/Svg';
import TooYoungSvg from '/static/icons/circle-too-young.svg';
import TaxiSvg from '/static/icons/circle-too-taxi.svg';
import ModifiedCarSvg from '/static/icons/circle-too-modified.svg';
import TooExpSvg from '/static/icons/circle-too-expensive.svg';

import {
  updateCustomer,
  loadCustomerFromStorage,
  createCustomer,
  clearStatuses
} from 'state/Customer.action';
import useTokenRefresh from 'state/useTokenRefresh';

AddFreshChat();

const QuoteApi = CoveApi('Quote');

const onUpdateSuccess = ({ appContext, customerContext, customer }) => {
  const queryStringParameters = queryString.parse(window.location.search);
  const risk = appContext.risk;
  const quote = appContext.quote;

  if (queryStringParameters && queryStringParameters.redirect == 'details') {
    // try to update the customer
    const quote = appContext.quote;

    quote.customer = customer;

    // reattach the referral-code
    if (localStorage.getItem('cove-referral-code')) {
      quote.referral_code = localStorage.getItem('cove-referral-code');
    }

    // update the actual quote
    QuoteApi.useLocalToken()
      .update(quote)
      .then(updatedQuote => {
        appContext.setState({ quote: updatedQuote });
        clearStatuses(customerContext.dispatch)();

        return Router.push(
          queryStringParameters.path === 'vehicle' ? '/quote/details-summary' : '/quote/summary'
        );
      });
  } else {
    clearStatuses(customerContext.dispatch)();
    if (risk.item) {
      // Prepropulate item if we already have a quote;
      if (quote && quote.coverages && quote.coverages[0].risk) {
        risk.item = quote.coverages[0].risk.item;
      } else {
        // Otherwise make sure we have a clean item;
        risk.item = {};
      }
      localStorage.setItem('cove-risk', JSON.stringify(risk));
      Router.push('/quote/item');
    } else if (risk.vehicle) Router.push('/quote/multi-policy-discount?product=vehicle');
    else if (risk.pet) Router.push('/quote/multi-policy-discount?product=pet');
  }
};

const submitName = ({ customer, firstName, lastName, appContext, customerContext }) => {
  return async () => {
    // Stop the whole damn page from reloading
    const risk = appContext.risk;

    let mixProduct = risk.vehicle ? 'Vehicle' : 'Pet';
    Mixpanel.register({ Product: mixProduct });
    Mixpanel.track(`Submitted Name (${mixProduct})`, {
      Value: firstName + ' ' + lastName,
      'First Name': firstName,
      Surname: lastName
    });
    Mixpanel.time_event('Quote Complete (Vehicle)');
    Mixpanel.time_event('Purchased Policy (Vehicle)');
    Mixpanel.people.set({ $name: firstName + ' ' + lastName });
    Mixpanel.register({ Name: firstName + ' ' + lastName });

    // lets make localstorage the source of truth
    const customer_id = localStorage.getItem('cove-customer-id');
    logger.info('Name', 'Customer ID is', customer_id);
    if (!customer_id) {
      const web_id = uuidv4();
      localStorage.setItem('cove-web-id', web_id);

      const customerPayload = {
        first_name: firstName,
        last_name: lastName,
        web_id
      };

      await createCustomer(customerContext.dispatch)(customerPayload);
    } else {
      await updateCustomer(customer, customerContext.dispatch)({
        first_name: firstName,
        last_name: lastName,
        referral_code: localStorage.getItem('cove-referral-code')
      });
    }

    return false;
  };
};

const startOver = appContext => {
  logger.info('Name', 'Start Over Clicked');
  clearMultipleLocalStorage('cove-access-token', 'cove-customer-id', 'cove-quote-id');

  appContext.setState({
    firstName: '',
    lastName: '',
    email: '',
    customer: null,
    quote: null
  });
};

const NamePage = () => {
  const appContext = useContext(Context);
  const customerContext = useContext(CustomerContext);

  const {
    customer,
    customerFetchStatus,
    customerUpdateStatus,
    customerCreateStatus
  } = customerContext ? customerContext : {};

  const { loggedIn, risk = {} } = appContext ? appContext : {};

  const [firstName, setFirstName] = useState(customer ? customer.first_name : '');
  const [lastName, setLastName] = useState(customer ? customer.last_name : '');
  const [errorFirstName, setErrorFirstName] = useState(false);
  const [errorLastName, setErrorLastName] = useState(false);
  const [errorName, setErrorName] = useState(null);
  const [permissionError, setPermissionError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInInvalidState, setIsInInvalidState] = useState(false);

  const { addToast } = useToasts();

  useQueryStringProduct();
  const { tokenBroken } = useTokenRefresh();

  useEffect(() => {
    if (tokenBroken) {
      clearMultipleLocalStorage(
        'cove-access-token',
        'cove-customer-id',
        'cove-quote-id',
        'cove-web-id'
      );
    }
  }, [tokenBroken]);

  useEffect(() => {
    let { partnercode, referralcode, product } = queryString.parse(window.location.search);
    let risk;
    let updateObj;

    if (!product || product == 'vehicle') {
      product = 'vehicle';
      risk = { vehicle: {} };
      updateObj = { risk, selectedProduct: 'vehicle' };
    } else if (product == 'pet') {
      product = 'pet';
      risk = { pet: {} };
      updateObj = { risk, selectedProduct: 'pet' };
    }

    if (!loggedIn) {
      if (
        partnercode == '68BB99' ||
        referralcode == '68BB99' ||
        partnercode == '9EB123' ||
        referralcode == '9EB123' ||
        partnercode == 'CFC8BF' ||
        referralcode == 'CFC8BF'
      ) {
        localStorage.clear();

        localStorage.setItem('cove-referral-code', partnercode ? partnercode : referralcode);
        Mixpanel.track('Cleared storage for Partner quote');
      }
    }

    if (partnercode == 'C9258D' || referralcode == 'C9258D') {
      localStorage.clear();
      Mixpanel.track('Cleared storage for Cove Admin Code');
    }
    if (['vehicle', 'pet'].includes(product)) {
      localStorage.setItem('cove-risk', JSON.stringify(risk));
      let code;
      if (partnercode) {
        code = partnercode;
      } else if (referralcode) {
        code = referralcode;
      }

      if (code && code != 'C9258D') {
        updateObj.referral_code = code;
        localStorage.setItem('cove-referral-code', code);
      }

      if (code && code == 'voucher') {
        localStorage.removeItem('cove-referral-code');
      }

      if (
        localStorage.getItem('cove-referral-code') == 'voucher' ||
        localStorage.getItem('cove-referral-code') == 'null'
      ) {
        localStorage.removeItem('cove-referral-code');
      }

      appContext.setState(updateObj);
    } else if (appContext.selectedProduct === '' || appContext.selectedProduct === undefined) {
      Sentry.captureException(
        `Incorrect product value given for name page queryparameter.... Product: ${product}`
      );
      Router.push('/quote/name?product=vehicle');
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      await loadCustomerFromStorage(customerContext.dispatch)();
    };

    run();
  }, []);

  // when user is created
  useEffect(() => {
    if (customerCreateStatus === 'pending') {
      setLoading(true);
    } else if (customerCreateStatus === 'success') {
      // TODO: appContext should use reducers and not update directly
      appContext.setState({
        firstName: customer.first_name,
        lastName: customer.last_name,
        customer: customer
      });
      setLoading(false);
      onUpdateSuccess({ appContext, customerContext, customer });
    } else if (customerCreateStatus === 'failed') {
      setLoading(false);
    }
  }, [customerCreateStatus]);

  // When customer is being loaded
  useEffect(() => {
    if (customerFetchStatus === 'pending') {
      setLoading(true);
    } else if (customerFetchStatus === 'success') {
      setFirstName(customer.first_name);
      setLastName(customer.last_name);
      // TODO: appContext should use reducers and not update directly
      appContext.setState({
        firstName: customer.first_name,
        lastName: customer.last_name,
        customer: customer
      });
      setLoading(false);
    } else if (customerFetchStatus === 'failed') {
      setLoading(false);
      onFetchError(customerContext.error);
    }
  }, [customerFetchStatus]);

  // When customer is updating
  useEffect(() => {
    if (customerUpdateStatus === 'pending') {
      setLoading(true);
    } else if (customerUpdateStatus === 'success') {
      // TODO: appContext should use reducers and not update directly
      appContext.setState({
        firstName: customer.first_name,
        lastName: customer.last_name,
        customer: customer
      });
      setLoading(false);
      onUpdateSuccess({ appContext, customerContext, customer });
    } else if (customerUpdateStatus === 'failed') {
      setLoading(false);
      onFetchError(customerContext.error);
    }
  }, [customerUpdateStatus]);

  // Update freshchat and error message
  useEffect(() => {
    if (window.fcWidget) {
      window.fcWidget.user.setProperties({
        firstName: firstName,
        lastName: lastName
      });
    }

    if (!firstName && !lastName) setErrorName(formatError());
    else if (!firstName) setErrorName(formatError());
    else if (!lastName) setErrorName(formatError());
    else return setErrorName(noError());
  }, [firstName, lastName]);

  const onFetchError = error => {
    logger.info('Name', 'Fetch Error', error);
    if (error.statusCode === 403) {
      if (error.message.includes('User is not authorized to access this resource')) {
        setIsInInvalidState(true);
        addToast('Session is in an invalid state. Please start over.', { appearance: 'error' });
        clearMultipleLocalStorage('cove-customer-id', 'cove-access-token', 'cove-quote-id');
        Mixpanel.track('Unauthorised - Customer ID Cleared');
      } else {
        setPermissionError(true);
      }
    } else if (error.statusCode === 401) {
      clearMultipleLocalStorage('cove-customer-id', 'cove-access-token', 'cove-quote-id');
      Mixpanel.track('Session Timeout - New User Will Be Created');
    }
  };

  const onChangeFirstName = evt => {
    setFirstName(evt.target.value);
    if (evt.target.value === '') {
      setErrorFirstName(true);
    } else {
      setErrorFirstName(false);
    }
  };

  const onChangeLastName = evt => {
    setLastName(evt.target.value);
    if (evt.target.value === '') {
      setErrorLastName(true);
    } else {
      setErrorLastName(false);
    }
  };

  const onSubmitName = async evt => {
    evt.preventDefault();

    // save data
    await submitName({
      customer,
      appContext,
      customerContext,
      firstName,
      lastName
    })();
  };

  const onStartOver = evt => {
    startOver(appContext);
    setPermissionError(false);
  };

  const notInsurableInfo = [
    {
      text: 'Drivers under 21',
      icon: <Svg className="svg-teal" src={TooYoungSvg} height="120" width="120" />
    },
    {
      text: 'Commercial use (including Uber and Uber Eats)',
      icon: <Svg className="svg-teal" src={TaxiSvg} height="120" width="120" />
    },
    {
      text: 'Modified cars',
      icon: <Svg className="svg-teal" src={ModifiedCarSvg} height="120" width="120" />
    },
    {
      text: 'Cars worth more than $130k',
      icon: <Svg className="svg-teal" src={TooExpSvg} height="120" width="120" />
    }
  ];

  return (
    <>
      <Head>
        <title>Name | Cove</title>
      </Head>
      <NavBar />
      <div className="content-box">
        <div className="main-content">
          <Section>
            <FormContainer onSubmit={onSubmitName}>
              <Title>
                {loggedIn
                  ? `Welcome back ${firstName} ${lastName}`
                  : 'Before we get started, what’s your name?'}
              </Title>
              <NameField
                firstName={firstName}
                lastName={lastName}
                onChangeFirstName={onChangeFirstName}
                onChangeLastName={onChangeLastName}
                requiredFirstName={errorFirstName}
                requiredLastName={errorLastName}
                errorName={errorFirstName || errorLastName}
                disabled={loading || loggedIn}
                submitAttempted={false}
                autoTitleCase={true}
              />

              <p style={{ marginTop: '30px' }}>
                <span>
                  By continuing, you agree to our{' '}
                  <a
                    href="https://www.coveinsurance.co.nz/terms-of-use/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {' '}
                    Terms of Use
                  </a>
                </span>
              </p>

              {permissionError ? (
                <Message>
                  Sorry, it appears that you are already a customer. Would you like to{' '}
                  <Link
                    href={`/user/login?redirect=/quote/${
                      risk && risk.vehicle ? 'licence-plate' : 'pet-details'
                    }`}
                  >
                    <a>Login</a>
                  </Link>{' '}
                  or&nbsp;<a onClick={onStartOver}>Start Over</a>&nbsp;fresh?
                </Message>
              ) : (
                ''
              )}

              <SubmitButton
                error={errorName}
                loading={loading}
                marginTop={'30px'}
                disabled={permissionError || isInInvalidState}
              >
                Ok! Let's get started
              </SubmitButton>

              <div className="with-top-margin">
                {loggedIn ? null : (
                  <Link
                    href={`/user/login?redirect=/quote/${
                      risk && risk.vehicle ? 'licence-plate' : 'pet-details'
                    }`}
                  >
                    <a>Already a customer? Login here</a>
                  </Link>
                )}
              </div>
            </FormContainer>
          </Section>
        </div>

        {risk.vehicle ? (
          <>
            <div className="body-content">
              <Section background="has-background-cove" minHeight="450px">
                <FormContainer>
                  <Subtitle marginTop="36px" marginBottom="29px" maxWidth="700px">
                    To save you some time, please note that we only provide <b>comprehensive</b>{' '}
                    insurance, and don't insure:
                  </Subtitle>
                  <div className="columns container is-multiline level-item">
                    <ContentCards items={notInsurableInfo} />
                  </div>
                </FormContainer>
                <FooterLearnMoreAboutCarIns marginTop="36px" marginBottom="33px" padding="0" />
              </Section>
            </div>
            {/* <div className="footer-content" /> */}
          </>
        ) : (
          <FooterPeopleFlockToCove />
        )}
      </div>

      <ProgressBar progress="10" />
      <BackChevron loading={loading} />

      <style jsx="true">{`
        .content-box {
          display: flex;
          flex-flow: column;
          height: 100vh;
        }

        .content-box .main-content {
          flex: 0 1 auto;
        }

        .content-box .body-content {
          flex: 1 1 auto;
          background-color: #edf6f3;
          margin-top: 50px;
          margin-bottom: -60px;
        }

        .content-box .footer-content {
          flex: 0 1 40px;
        }

        .with-top-margin {
          display: block;
          margin-top: 20px;
        }

        @media all and (max-width: 768px) {
          .with-top-margin {
            margin-top: 22px;
          }
        }
      `}</style>
    </>
  );
};

export default NamePage;
